import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { AutoSizer, List } from 'react-virtualized';
import {
  Paper,
} from '@material-ui/core';
import LineReceptionTableRowWrapper from './lineReceptionTableRowWrapper';
import LineReceptionTableHeader from './lineReceptionTableHeader';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0 32px',
  },
  paper: {
    width: '100%',
    boxShadow: 'none',
  },
  control: {
    position: 'absolute',
    top: '-16px',
    right: 0,
    display: 'flex',
    alignItems: 'center',
  },
  controlLbl: {
    color: 'rgba(51, 51, 51, 0.6)',
    fontFamily: 'Roboto',
    fontSize: 13,
    lineHeight: 1.6,
    letterSpacing: 0,
    marginRight: 8,
  },
  tablePagination: {
    '& .MuiTablePagination-spacer + .MuiTablePagination-caption': {
      display: 'none',
    },
    '& .MuiTablePagination-toolbar': {
      paddingLeft: 0,
    },
    '& .MuiTablePagination-selectRoot': {
      margin: '0 40px 0 0',
    },
    '& .MuiTypography-root': {
      fontSize: '13px',
      lineHeight: '1.6',
      letterSpacing: '0',
      color: 'rgba(51, 51, 51, 0.6)',
    },
    '& .MuiSelect-root': {
      fontSize: '13px',
      lineHeight: '1.6',
      letterSpacing: '0',
      color: 'rgba(51, 51, 51, 0.6)',
    },
    '& .MuiIconButton-root': {
      padding: '0 5px',
    },
  },
  tableHead: {
    '&::after': {
      content: '""',
      height: 32,
      width: '100%',
      display: 'block',
    },
  },
  tableCellTh: {
    position: 'relative',
    fontFamily: 'Roboto',
    fontSize: '13px',
    fontWeight: '700',
    lineHeight: '1.6',
    padding: 0,
    textAlign: 'center',
    borderBottom: '2px solid #C8C8C8',
    color: '#8C8C8C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    '&:nth-child(1)': { width: 40, minWidth: 40, maxWidth: 40 },
    '&:nth-child(2)': { width: 140, minWidth: 140, maxWidth: 140 },
    '&:nth-child(3)': { width: 70, minWidth: 70, maxWidth: 70 },
    '&:nth-child(4)': { width: 280, minWidth: 280, maxWidth: 280 },
    '&:nth-child(5)': { width: 100, minWidth: 100, maxWidth: 100 },
    '&:nth-child(6)': { width: 280, minWidth: 280, maxWidth: 280 },
    '&:nth-child(7)': { width: 100, minWidth: 100, maxWidth: 100 },
    '&:nth-child(8)': { width: 100, minWidth: 100, maxWidth: 100 },
    '&:nth-child(9)': { width: 100, minWidth: 100, maxWidth: 100 },
    '&:nth-child(10)': {
      flexGrow: 1,
      flexBasis: 0,
      minWidth: 200,
    },
    '&:nth-child(11)': { width: 100, minWidth: 100, maxWidth: 100 },
  },
  button: {
    background: '#F3F3F3',
    borderTop: '2px solid #8C8C8C',
    padding: '8px 0 8px 6px',
    borderRadius: 0,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  buttonIcon: {
    color: '#333',
    fontSize: 14,
    margin: '0 2px 0 0',
  },
  buttonTxt: {
    fontFamily: 'Roboto',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: 0,
  },
  tableSortLabel: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '& .MuiTableSortLabel-icon': {
      position: 'relative',
      marginRight: '-18px',
      marginLeft: '4px',
    },
  },
  checkBox1: {
    padding: '4px 8px',
    '&.Mui-checked': {
      color: theme.palette.primaryColor,
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  tableContainer: {
    height: 'calc(100vh - 140px)',
    width: '100%',
  },
  thead: {
    display: 'table-header-group',
    background: '#fafafa',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
}));

/**
 * 2つの要素を比較し、降順（大きい順）の並び順を決定する比較関数。
 * 特定のキー（memberStatusCode）では業務ロジックに基づいた数値補完を行う。
 */
function descendingComparator(a, b, orderBy) {
  let targetKey = orderBy;
  if (orderBy === 'customerId') {
    targetKey = 'id';
  }
  if (orderBy === 'customer') {
    targetKey = 'fullName'; // 顧客列のソートでは顧客のフルネームでソートする
  }

  // データの場所を特定（直下か、customer内か）
  let a2 = (a[targetKey] !== undefined) ? a[targetKey] : a.customer?.[targetKey];
  let b2 = (b[targetKey] !== undefined) ? b[targetKey] : b.customer?.[targetKey];

  // 数値項目のソート（IDや予算、年収など）
  // フロントでのキーとAPIのキーを考慮
  const numericFields = ['id', 'customerId', 'landBudgetMax', 'detachedHouseBudgetMax', 'annualIncome'];
  if (numericFields.includes(orderBy)) {
    a2 = Number(a2) || 0;
    b2 = Number(b2) || 0;
  }

  // 最終受信日時のソート（「分前」対応）
  if (orderBy === 'lastReceivedAt') {
    const toTimestamp = (val) => {
      if (!val) return 0;
      if (typeof val === 'string' && val.includes('分前')) {
        const m = val.match(/\d+/);
        return m ? Date.now() - (Number(m[0]) * 60000) : 0;
      }
      const date = new Date(val);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };
    a2 = toTimestamp(a2);
    b2 = toTimestamp(b2);
  }

  // 特殊なステータスコード
  if (orderBy === 'memberStatusCode') {
    a2 = a2 ?? 4;
    b2 = b2 ?? 4;
  }

  // 比較実行
  if (b2 < a2) return -1;
  if (b2 > a2) return 1;
  return 0;
}

/**
 * 昇順(asc)・降順(desc)の設定に応じて、適切な比較関数を生成する高階関数。
 */
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * 元の順番を維持したまま並び替えを行う（安定ソート）。
 * 同じ値を持つ要素同士が、並び替え後に不必要に入れ替わるのを防ぐ。
 */
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function LineReceptionTableWrapper(props) {
  const {
    rows,
  } = props;

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('lastReceivedAt');
  const classes = useStyles();
  const customerList = stableSort(rows, getComparator(order, orderBy));

  /**
   * ヘッダーがクリックされた時に、ソートの向き(asc/desc)と対象項目を更新する
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (customerList.length === 0) return <div />;
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div style={{ height: 'calc(100vh - 140px)', width: '100%' }}>
          <AutoSizer>
            {({ width, height }) => {
              const contentsWidth = Math.max(width, 1510);
              return (
                <div style={{ width, height, overflowX: 'auto', overflowY: 'hidden' }}>
                  <div style={{ width: contentsWidth, height: '100%' }}>
                    <LineReceptionTableHeader
                      classes={classes}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <List
                      width={contentsWidth}
                      height={height - 40}
                      rowCount={customerList?.length ?? 0}
                      rowHeight={52}
                      overscanRowCount={5}
                      // eslint-disable-next-line react/no-unstable-nested-components
                      rowRenderer={({ index, style }) => (
                        <LineReceptionTableRowWrapper
                          key={customerList[index].customerId}
                          row={customerList[index]}
                          index={index}
                          style={style}
                        />
                      )}
                    />
                  </div>
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </Paper>
    </div>
  );
}
